import { useState } from 'react'
import { download, query } from '../../api/client'
import { useQuery } from '../../api/hooks'
import type { ReportBasis, TrialBalance } from '../../api/types'
import { useAuth } from '../../auth/AuthContext'
import { P } from '../../auth/permissions'
import { ErrorBanner, Loading, Money, Panel, money, useDateRange } from '../../components/ui'

export function TrialBalancePage() {
  const { can } = useAuth()
  const { from, to, setFrom, setTo } = useDateRange()
  const [basis, setBasis] = useState<ReportBasis>('Accrual')
  const [includeZero, setIncludeZero] = useState(false)

  const report = useQuery<TrialBalance>(
    `/reports/trial-balance${query({ from, to, basis, includeZero })}`,
    [from, to, basis, includeZero],
  )

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label>To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="field">
          <label>Basis</label>
          <select value={basis} onChange={(e) => setBasis(e.target.value as ReportBasis)}>
            <option value="Accrual">Accrual</option>
            <option value="Cash">Cash</option>
          </select>
        </div>

        <label style={{ textTransform: 'none', fontWeight: 400, fontSize: 13, paddingBottom: 6 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginRight: 6 }}
            checked={includeZero}
            onChange={(e) => setIncludeZero(e.target.checked)}
          />
          Include nil accounts
        </label>

        <div className="spacer" />

        {can(P.reports.export) && (
          <button
            onClick={() =>
              void download(
                `/reports/trial-balance/excel${query({ from, to, basis, includeZero })}`,
                'trial-balance.xlsx',
              )
            }
          >
            Export to Excel
          </button>
        )}
      </div>

      <ErrorBanner error={report.error} />
      {report.loading && <Loading />}

      {report.data && (
        <>
          {!report.data.isBalanced && (
            <div className="banner error">
              <strong>The trial balance does not balance.</strong> It is out by{' '}
              {money(report.data.difference)}. Nothing else in the accounts can be trusted until
              this is resolved.
            </div>
          )}

          <Panel flush>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>Code</th>
                    <th>Account</th>
                    <th className="num">Opening Dr</th>
                    <th className="num">Opening Cr</th>
                    <th className="num">Period Dr</th>
                    <th className="num">Period Cr</th>
                    <th className="num">Closing Dr</th>
                    <th className="num">Closing Cr</th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.rows.map((row) => (
                    <tr key={row.accountId}>
                      <td className="code">{row.code}</td>
                      <td>{row.nameEn}</td>
                      <td className="num">
                        <Money value={row.openingDebit} blankZero />
                      </td>
                      <td className="num">
                        <Money value={row.openingCredit} blankZero />
                      </td>
                      <td className="num">
                        <Money value={row.periodDebit} blankZero />
                      </td>
                      <td className="num">
                        <Money value={row.periodCredit} blankZero />
                      </td>
                      <td className="num strong">
                        <Money value={row.closingDebit} blankZero />
                      </td>
                      <td className="num strong">
                        <Money value={row.closingCredit} blankZero />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={6}>Total</td>
                    <td className="num">
                      <Money value={report.data.totalDebit} />
                    </td>
                    <td className="num">
                      <Money value={report.data.totalCredit} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Panel>
        </>
      )}
    </>
  )
}
